/**
 * Editorial Authoring V2 — HERO crop modal (BUILD 2).
 *
 * V2-only. Does not import the legacy CropModal
 * (@/components/editorial/CropModal) or anything else from
 * @/components/editorial/ — only the Owner-approved Emergent crop
 * UX/mechanics were used as the reference, not its code/architecture.
 *
 * WYSIWYG contract: the image is rendered with object-fit: cover +
 * object-position: {focal_x*100}% {focal_y*100}% + a transform: scale
 * around that same focal point — the same mechanism the existing HERO
 * focal/zoom fields already imply (settings.focal_x/focal_y/hero_zoom
 * for desktop, settings.mobile_focal_x/mobile_focal_y/hero_mobile_zoom
 * for mobile). What's visible inside the frame here is exactly what
 * those existing fields already mean; this modal does not introduce a
 * new interpretation of them. object-fit: cover also guarantees the
 * photo's own proportions are preserved — never stretched.
 */
import { useEffect, useRef, useState } from "react";

function clamp01(v) {
  return Math.max(0, Math.min(1, v));
}

export function EditorialCropV2({
  open, url, aspectRatio, frameLabel,
  focalX = 0.5, focalY = 0.5, zoom = 1,
  onSave, onClose,
}) {
  const [fx, setFx] = useState(focalX);
  const [fy, setFy] = useState(focalY);
  const [z, setZ] = useState(zoom);
  const [dragging, setDragging] = useState(false);
  const frameRef = useRef(null);
  const dragStart = useRef(null);

  useEffect(() => {
    if (!open) return;
    setFx(focalX);
    setFy(focalY);
    setZ(zoom);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, url]);

  useEffect(() => {
    if (!dragging) return undefined;

    const handleMove = (clientX, clientY) => {
      const frame = frameRef.current;
      if (!frame || !dragStart.current) return;
      const rect = frame.getBoundingClientRect();
      const dx = (clientX - dragStart.current.x) / rect.width;
      const dy = (clientY - dragStart.current.y) / rect.height;
      setFx(clamp01(dragStart.current.fx - dx / z));
      setFy(clamp01(dragStart.current.fy - dy / z));
    };

    const onMouseMove = (e) => handleMove(e.clientX, e.clientY);
    const onTouchMove = (e) => {
      if (e.touches[0]) handleMove(e.touches[0].clientX, e.touches[0].clientY);
    };
    const stopDrag = () => setDragging(false);

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", stopDrag);
    window.addEventListener("touchmove", onTouchMove, { passive: true });
    window.addEventListener("touchend", stopDrag);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", stopDrag);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", stopDrag);
    };
  }, [dragging, z]);

  if (!open) return null;

  const startDrag = (clientX, clientY) => {
    dragStart.current = { x: clientX, y: clientY, fx, fy };
    setDragging(true);
  };

  const reset = () => { setFx(0.5); setFy(0.5); setZ(1); };
  const done = () => onSave?.({ focal_x: fx, focal_y: fy, zoom: z });

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 px-4 py-6"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      data-testid="editorial-v2-crop">
      <div className="flex max-h-[92vh] w-full max-w-md flex-col overflow-hidden rounded-3xl bg-cream">
        <div className="flex items-center justify-between border-b border-espresso/10 px-4 py-3">
          <div className="text-sm font-medium text-espresso">Настроить кадр</div>
          {frameLabel && <div className="text-[11px] text-espresso/50">{frameLabel}</div>}
        </div>

        <div className="overflow-y-auto px-4 py-4">
          <div
            ref={frameRef}
            className="relative mx-auto w-full max-w-sm select-none overflow-hidden rounded-2xl bg-black"
            style={{ aspectRatio, touchAction: "none" }}
            onMouseDown={(e) => startDrag(e.clientX, e.clientY)}
            onTouchStart={(e) => { if (e.touches[0]) startDrag(e.touches[0].clientX, e.touches[0].clientY); }}
            data-testid="editorial-v2-crop-frame"
          >
            {url && (
              <img
                src={url}
                alt=""
                draggable={false}
                className="absolute inset-0 h-full w-full"
                style={{
                  objectFit: "cover",
                  objectPosition: `${fx * 100}% ${fy * 100}%`,
                  transform: `scale(${z})`,
                  transformOrigin: `${fx * 100}% ${fy * 100}%`,
                  cursor: dragging ? "grabbing" : "grab",
                }}
              />
            )}
            <div className="pointer-events-none absolute inset-0">
              <div className="absolute left-1/3 top-0 h-full w-px bg-white/40" />
              <div className="absolute left-2/3 top-0 h-full w-px bg-white/40" />
              <div className="absolute top-1/3 left-0 h-px w-full bg-white/40" />
              <div className="absolute top-2/3 left-0 h-px w-full bg-white/40" />
            </div>
          </div>

          <div className="mx-auto mt-4 w-full max-w-sm">
            <label className="mb-1 block text-[11px] text-espresso/60">Масштаб</label>
            <input
              type="range" min={1} max={3} step={0.05} value={z}
              onChange={(e) => setZ(Number(e.target.value))}
              className="w-full"
              data-testid="editorial-v2-crop-zoom"
            />
          </div>
        </div>

        <div className="flex items-center justify-between gap-2 border-t border-espresso/10 px-4 py-3">
          <button type="button" onClick={reset}
            className="rounded-lg border border-espresso/20 bg-white px-3 py-2 text-xs text-espresso"
            data-testid="editorial-v2-crop-reset">
            Сбросить
          </button>
          <div className="flex items-center gap-2">
            <button type="button" onClick={onClose}
              className="rounded-lg border border-espresso/20 bg-white px-3 py-2 text-xs text-espresso"
              data-testid="editorial-v2-crop-cancel">
              Отмена
            </button>
            <button type="button" onClick={done}
              className="rounded-lg bg-espresso px-4 py-2 text-xs text-cream"
              data-testid="editorial-v2-crop-done">
              Готово
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
